/**
 * SessionBriefSuggestionCard — Renderer für 'session_brief'-Suggestions
 * aus cap-prepare-session-brief.
 *
 * Zeigt:
 *   - Patienten-Summary (2–3 Sätze)
 *   - Red-Flags (falls vorhanden)
 *   - vorgeschlagene Themen mit Begründung
 *   - "Patientenakte öffnen" als primäre Aktion
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, BookOpen, ChevronRight, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAiCompanion } from '@/contexts/AiCompanionContext';
import {
  isSessionBriefPayload,
  type AiSuggestion,
} from '@/lib/ai/types';

interface Props {
  suggestion: AiSuggestion;
}

export const SessionBriefSuggestionCard: React.FC<Props> = ({ suggestion }) => {
  const navigate = useNavigate();
  const { resolve } = useAiCompanion();

  if (!isSessionBriefPayload(suggestion.payload)) return null;
  const p = suggestion.payload;

  const handlePrimary = async () => {
    if (suggestion.patient_id) {
      navigate(`/patients/${suggestion.patient_id}`);
    }
    await resolve(suggestion, 'accepted', 'open_patient');
  };

  const handleDismiss = async () => {
    await resolve(suggestion, 'dismissed');
  };

  return (
    <article className="border-b p-4 last:border-b-0">
      <header className="mb-2 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-medium">Sitzungsvorbereitung</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Hinweis schließen"
          onClick={handleDismiss}
          className="h-6 w-6"
        >
          <X className="h-4 w-4" />
        </Button>
      </header>

      <p className="mb-2 text-sm text-foreground">{p.patient_summary}</p>

      {p.red_flags.length > 0 && (
        <div className="mb-2 flex flex-col gap-1 rounded-md bg-rose-50 px-2 py-1.5 dark:bg-rose-950/50">
          {p.red_flags.map((rf, i) => (
            <div
              key={i}
              className="flex items-start gap-1.5 text-xs text-rose-900 dark:text-rose-100"
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{rf}</span>
            </div>
          ))}
        </div>
      )}

      {p.suggested_topics.length > 0 && (
        <ul className="mb-2 space-y-1.5 text-xs">
          {p.suggested_topics.slice(0, 4).map((t, i) => (
            <li key={i}>
              <span className="font-medium text-foreground">{t.title}</span>
              <span className="text-muted-foreground"> — {t.reason}</span>
            </li>
          ))}
        </ul>
      )}

      {p.open_items.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1">
          {p.open_items.slice(0, 4).map((it, i) => (
            <Badge key={i} variant="secondary" className="text-[10px] font-normal">
              {it}
            </Badge>
          ))}
        </div>
      )}

      <Button size="sm" onClick={handlePrimary}>
        Patientenakte öffnen
        <ChevronRight className="ml-1 h-4 w-4" />
      </Button>
    </article>
  );
};
